/**
 * f13.2: the "Submit cycle" page action and its confirmation (prototype
 * `.modal-scrim` / `.modal`). Rendered only when the server offers `SUBMIT`
 * (Finance on a Frozen cycle); a forced POST is still refused server-side.
 *
 * The outcome toast outlives the button: once the cycle is Submitted the
 * button disappears, but the "submitted" message stays until dismissed.
 */

import { useState } from 'react';
import { CheckCircle2, Loader2, Send, TriangleAlert, X } from 'lucide-react';

import type { Cycle } from '@/api/client';
import { useCycle } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import '@/styles/cycle-export.css';

import { canSubmit, useSubmitCycle } from './useCycleSubmission';

function SubmitConfirmation({
  cycle,
  pending,
  onClose,
  onConfirm,
}: {
  cycle: Cycle;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const snapshot = cycle.current_snapshot;
  return (
    <Modal
      title={`Submit cycle ${cycle.code}?`}
      subtitle={`Finance only · ${cycle.derived.label}`}
      onClose={onClose}
      onSubmit={onConfirm}
      busy={pending}
      submitLabel="Submit cycle"
      submitIcon={pending ? <Loader2 className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
      narrow
      layout="plain"
    >
      <div className="alert alert-warning">
        <TriangleAlert aria-hidden="true" />
        <div>
          <div className="a-title">Submitted is final</div>
          <div className="a-body">
            Submit cycle {cycle.code}? The cycle moves to Submitted, which is final and cannot be reopened.
            The CSV is generated from the frozen figures
            {snapshot ? ` (snapshot #${snapshot.sequence}, ${snapshot.line_count} forecast lines)` : ''}.
            Atlas does not send it to COSMA — download it and upload it by hand.
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function SubmitCycleButton({ cycle }: { cycle: Cycle }) {
  const [open, setOpen] = useState(false);
  const { submit, outcome, dismiss } = useSubmitCycle(cycle.id, cycle.code);

  return (
    <>
      {canSubmit(cycle) && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setOpen(true)}
          disabled={submit.isPending}
          aria-busy={submit.isPending}
        >
          {submit.isPending ? <Loader2 className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />} Submit
          cycle
        </button>
      )}

      {open && (
        <SubmitConfirmation
          cycle={cycle}
          pending={submit.isPending}
          onClose={() => setOpen(false)}
          onConfirm={() => submit.mutate(cycle.version, { onSettled: () => setOpen(false) })}
        />
      )}

      {outcome && (
        <div
          className={`alert ${outcome.tone === 'success' ? 'alert-success' : 'alert-danger'} cx-toast`}
          role={outcome.tone === 'success' ? 'status' : 'alert'}
        >
          {outcome.tone === 'success' ? <CheckCircle2 aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
          <div className="a-title">{outcome.message}</div>
          <button type="button" className="cx-toast-close" onClick={dismiss} aria-label="Dismiss">
            <X aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}

/**
 * The submission on the f12.1 review. The page renders it only when the
 * review's server-computed `can_submit` is true (Frozen, Finance), so the
 * cycle -- a Finance/Admin read -- is fetched only for a caller the route admits.
 */
export function CycleSubmitAction({ cycleId }: { cycleId: string }) {
  const cycle = useCycle(cycleId);
  return cycle.data ? <SubmitCycleButton cycle={cycle.data} /> : null;
}
